import { useMemo, useState } from "react";

import { useCadastrarUsuario } from "../../../hooks/useCadastrarUsuario";
import { useCadastrarCota } from "../../../hooks/useCadastrarCota";
import { useCadastrarCotaUsuario } from "../../../hooks/useCadastrarCotaUsuario";
import { CotaUsuario } from "../../../interfaces/CotaUsuario";
import { Cota } from "../../../interfaces/Cota";
import { Usuario } from "../../../interfaces/Usuario";
import { Empreendimento } from "../../../interfaces/Empreendimento";
import { Apartamento } from "../../../interfaces/Apartamento";
import { useCadastrarEmpreendimento } from "../../../hooks/useCadastrarEmpreendimento";
import { useCadastrarApartamento } from "../../../hooks/useCadastrarApartamento";
import { Endereco } from "../../../interfaces/Endereco";
import { useCadastrarEndereco } from "../../../hooks/useCadastrarEndereco";
import { validateToken } from "../../../components/Globais";

export const useCadastrarPropriedade = () => {
  // Estados gerais
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const resetError = () => setError(null);
  const resetSuccess = () => setSuccess(null);

  // Hooks individuais de cadastro
  const {
    handleSubmit: handleCadastrarUsuario,
    isLoading: isLoadingProprietario,
    error: errorProprietario,
    success: successProprietario,
  } = useCadastrarUsuario();

  const {
    handleSubmit: handleCadastrarCota,
    isLoading: isLoadingCota,
    error: errorCota,
    success: successCota,
  } = useCadastrarCota();

  const {
    handleSubmit: handleCadastrarCotaUsuario,
    isLoading: isLoadingCotaUsuario,
    error: errorCotaUsuario,
    success: successCotaUsuario,
  } = useCadastrarCotaUsuario();

  const {
    handleSubmit: handleCadastrarEmpreendimento,
    isLoading: isLoadingEmpreendimento,
    error: errorEmpreendimento,
    success: successEmpreendimento,
  } = useCadastrarEmpreendimento();

  const {
    handleSubmit: handleCadastrarApartamento,
    isLoading: isLoadingApartamento,
    error: errorApartamento,
    success: successApartamento,
  } = useCadastrarApartamento();

  const {
    handleSubmit: handleCadastrarEndereco,
    isLoading: isLoadingEndereco,
    error: errorEndereco,
    success: successEndereco,
  } = useCadastrarEndereco();

  // Loading geral (se algum dos individuais estiver carregando)
  const isLoading = useMemo(
    () =>
      isLoadingProprietario ||
      isLoadingCota ||
      isLoadingCotaUsuario ||
      isLoadingEmpreendimento ||
      isLoadingApartamento ||
      isLoadingEndereco,
    [
      isLoadingProprietario,
      isLoadingCota,
      isLoadingCotaUsuario,
      isLoadingEmpreendimento,
      isLoadingApartamento,
      isLoadingEndereco,
    ]
  );

  // Error geral (concatena mensagens dos erros individuais)
  const aggregatedErrors = [
    errorProprietario,
    errorCota,
    errorCotaUsuario,
    errorEmpreendimento,
    errorApartamento,
    errorEndereco,
  ]
    .filter(Boolean)
    .join(" | ");

  // Success geral (concatena mensagens dos sucessos individuais)
  const aggregatedSuccess = [
    successProprietario,
    successCota,
    successCotaUsuario,
    successEmpreendimento,
    successApartamento,
    successEndereco,
  ]
    .filter(Boolean)
    .join(" | ");

  // Atualiza estados gerais quando os individuais mudam
  // Opcional: use useEffect para atualizar estados 'error' e 'success' se quiser centralizar

  // Funções específicas para cada cadastro
  const cadastrarProprietario = async (dados: Usuario) => {
    try {
      return await handleCadastrarUsuario(dados);
      setSuccess("Proprietário cadastrado com sucesso");
      resetError();
    } catch (err: any) {
      setError(err.message || "Erro ao cadastrar proprietário");
      setSuccess(null);
      throw err;
    }
  };

  const cadastrarRepresentante = async (dados: Usuario) => {
    try {
      return await handleCadastrarUsuario(dados);
      setSuccess("Representante cadastrado com sucesso");
      resetError();
    } catch (err: any) {
      setError(err.message || "Erro ao cadastrar representante");
      setSuccess(null);
      throw err;
    }
  };

  const cadastrarCota = async (dados: Cota) => {
    try {
      return await handleCadastrarCota(dados);
      setSuccess("Cota cadastrada com sucesso");
      resetError();
    } catch (err: any) {
      setError(err.message || "Erro ao cadastrar cota");
      setSuccess(null);
      throw err;
    }
  };

  const cadastrarCotaUsuario = async (dados: CotaUsuario) => {
    try {
      await handleCadastrarCotaUsuario(dados);
      setSuccess("Cota Usuário cadastrada com sucesso");
      resetError();
    } catch (err: any) {
      setError(err.message || "Erro ao cadastrar cota usuário");
      setSuccess(null);
      throw err;
    }
  };

  const cadastrarEmpreendimento = async (dados: Empreendimento) => {
    try {
      return await handleCadastrarEmpreendimento(dados);
      setSuccess("Empreendimento cadastrado com sucesso");
      resetError();
    } catch (err: any) {
      setError(err.message || "Erro ao cadastrar empreendimento");
      setSuccess(null);
      throw err;
    }
  };

  const cadastrarApartamento = async (dados: Apartamento) => {
    try {
      return handleCadastrarApartamento(dados);
      setSuccess("Apartamento cadastrado com sucesso");
      resetError();
    } catch (err: any) {
      setError(err.message || "Erro ao cadastrar apartamento");
      setSuccess(null);
      throw err;
    }
  };

  const cadastrarEndereco = async (dados: Endereco) => {
    try {
      await handleCadastrarEndereco(dados);
      setSuccess("Endereco cadastrado com sucesso");
      resetError();
    } catch (err: any) {
      setError(err.message || "Erro ao cadastrar apartamento");
      setSuccess(null);
      throw err;
    }
  };

  // Função para cadastrar tudo de uma vez (exemplo)
  const handleCadastrarTodos = async (
    dados: {
      empreendimento: Empreendimento;
      apartamento: Apartamento;
      cota: Cota;
      cotaUsuario: CotaUsuario;
      maisInfo: CotaUsuario;
      proprietario: Usuario;
      enderecoProprietario: Endereco;
      representante: Usuario;
      enderecoRepresentante: Endereco;
    },
    empreendimentoSistema: boolean,
    hasRepresentante: boolean,
    usuarioId: number
  ) => {
    try {
      let resultEmpreendimento;
      let resultAp;
      let resultCota;
      if (!empreendimentoSistema) {
        try {
          const dataEmpreendimento = { ...dados.empreendimento, origem: 0 };
          resultEmpreendimento = await cadastrarEmpreendimento(
            dataEmpreendimento
          );
        } catch (err: any) {
          setError("❌ Não foi possível cadastrar o empreendimento");
          throw err;
        }

        try {
          const dataAp = {
            empreendId: resultEmpreendimento.id,
            ...dados.apartamento,
            origem: 0,
          };
          resultAp = await cadastrarApartamento(dataAp);
        } catch (err: any) {
          setError("❌ Não foi possível cadastrar o apartamento");
          throw err;
        }
        try {
          const dataCota = {
            aptoId:
              resultAp && resultAp.id ? resultAp.id : dados.apartamento.id,
            ...dados.cota,
            origem: 0,
          };
          resultCota = await cadastrarCota(dataCota);
        } catch (err: any) {
          setError("❌ Não foi possível cadastrar a cota");
          throw err;
        }
      }

      // let resultProprietario;
      // try {
      //   resultProprietario = await cadastrarProprietario(dados.proprietario);
      // } catch (err: any) {
      //   setError("❌ Não foi possível cadastrar o proprietário");
      //   throw err;
      // }

      // try {
      //   const dataEnderecoProprietario = {
      //     usuarioId: resultProprietario.id,
      //     ...dados.enderecoProprietario,
      //   };
      //   await cadastrarEndereco(dataEnderecoProprietario);
      // } catch (err: any) {
      //   setError("❌ Não foi possível cadastrar o endereço do proprietário");
      //   throw err;
      // }

      if (hasRepresentante) {
        let resultRepresentante;
        try {
          resultRepresentante = await cadastrarRepresentante(
            dados.representante
          );
        } catch (err: any) {
          setError("❌ Não foi possível cadastrar o representante");
          throw err;
        }

        try {
          const dataEnderecoRepresentante = {
            usuarioId: resultRepresentante.id,
            ...dados.enderecoRepresentante,
          };
          await cadastrarEndereco(dataEnderecoRepresentante);
        } catch (err: any) {
          setError("❌ Não foi possível cadastrar o endereço do representante");
          throw err;
        }
      }

      try {
        const dataCotaUsuario = {
          ...dados.cotaUsuario,
          ...dados.maisInfo,
          cotaId: resultCota?.id ?? dados.cota.numeroCota,
          usuarioId: usuarioId,
          status: "AGUARDANDO_PERIODOS",
        };

        await cadastrarCotaUsuario(dataCotaUsuario);
      } catch (err: any) {
        setError("Não foi possível cadastrar a cota do usuário");
        throw err;
      }

      setSuccess("Propriedade cadastrada com sucesso!");
      resetError();
    } catch (err: any) {
      console.error(err);
      setSuccess(null);
      // Se quiser uma fallback:
      setError(err.message || "Erro desconhecido");
      throw err;
    }
  };

  return {
    cadastrarProprietario,
    cadastrarRepresentante,
    cadastrarCota,
    cadastrarCotaUsuario,
    cadastrarEmpreendimento,
    cadastrarApartamento,
    handleCadastrarTodos,
    isLoading,
    error: error || aggregatedErrors,
    success: success || aggregatedSuccess,
    resetError,
    resetSuccess,
  };
};
